import {
  EditorFeatures,
  EditorTableToolbar,
  HeadingToolbar,
  TextAlignmentToolbar,
} from '@src/ui/Editor';
import {
  BackgroundColorButton,
  BlockquoteButton,
  BoldButton,
  BulletListButton,
  FontColorButton,
  IFrameCodeButton,
  ImageButton,
  ItalicButton,
  LinkButton,
  OrderedListButton,
  TableButton,
  UnderlineButton,
  VideoButton,
} from '@src/ui/Editor/components/toolbars/buttons';
import { ActiveState, getActiveState, initialActiveState } from '@src/ui/Editor/utils/activeState';
import type { Editor } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface TenantToolbarEventHandlers {
  onSelectFile?: (files: FileList) => void;
  handleOpenLinkAddModal?: () => void;
  openConvertUrlModal?: () => void;
  openIFrameCodeModal?: () => void;
  openInsertTableModal?: () => void;
  handleSubmitConvertUrl?: () => void;
  handleSubmitIFrameCode?: () => void;
  handleInsertTable?: (params: { rows: number; cols: number }) => void;
}

export interface TenantToolbarProps {
  editor: Editor | null;
  features: EditorFeatures;
  eventHandlers: TenantToolbarEventHandlers;
}

/**
 * TenantToolbar
 * Full-featured toolbar for Tenant (Portal) context with all capabilities
 */
export const TenantToolbar: React.FC<TenantToolbarProps> = ({
  editor,
  features,
  eventHandlers,
}) => {
  const [activeState, setActiveState] = useState<ActiveState>(initialActiveState);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshActiveState = useCallback(() => {
    if (!editor) {
      return;
    }
    if (editor.view.composing) {
      return;
    }
    setActiveState(getActiveState(editor));
  }, [editor]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && eventHandlers?.onSelectFile) {
        eventHandlers.onSelectFile(files);
      }
      event.target.value = '';
    },
    [eventHandlers],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }

    refreshActiveState();
    editor.on('selectionUpdate', refreshActiveState);
    editor.on('update', refreshActiveState);

    return () => {
      editor.off('selectionUpdate', refreshActiveState);
      editor.off('update', refreshActiveState);
    };
  }, [editor, refreshActiveState]);

  if (!editor) {
    return null;
  }

  return (
    <div className='flex flex-col border-b'>
      {/* Hidden file input */}
      <input
        type='file'
        multiple
        className='hidden'
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      {features.richText && (
        <div className='toolbar flex flex-wrap gap-1 bg-gray-50 p-2'>
          {/* Heading Selector */}
          {features.textStyles && (
            <HeadingToolbar
              editor={editor}
              features={features}
            />
          )}

          {/* Bold */}
          {features.textStyles && (
            <BoldButton
              editor={editor}
              isActive={activeState.bold}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Italic */}
          {features.textStyles && (
            <ItalicButton
              editor={editor}
              isActive={activeState.italic}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Underline */}
          {features.textStyles && (
            <UnderlineButton
              editor={editor}
              isActive={activeState.underline}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Font Color */}
          {features.textStyles && <FontColorButton editor={editor} />}

          {/* Background Color */}
          {features.textStyles && <BackgroundColorButton editor={editor} />}

          {/* Text Alignment */}
          {features.textStyles && (
            <TextAlignmentToolbar
              editor={editor}
              features={features}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Bullet List */}
          {features.lists && (
            <BulletListButton
              editor={editor}
              isActive={activeState.bulletList}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Ordered List */}
          {features.lists && (
            <OrderedListButton
              editor={editor}
              isActive={activeState.orderedList}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Image Upload */}
          {features.images && <ImageButton onClick={() => fileInputRef.current?.click()} />}

          {/* Link */}
          {features.links && (
            <LinkButton
              isActive={activeState.link}
              refreshActiveState={refreshActiveState}
              onOpenLinkAddModal={() => eventHandlers?.handleOpenLinkAddModal?.()}
            />
          )}

          {/* Blockquote */}
          {features.quotes && (
            <BlockquoteButton
              editor={editor}
              isActive={activeState.blockquote}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Video URL */}
          {features.iframes && (
            <VideoButton
              onOpenConvertUrlModal={() => {
                if (eventHandlers?.openConvertUrlModal) {
                  eventHandlers.openConvertUrlModal();
                } else {
                  eventHandlers?.handleSubmitConvertUrl?.();
                }
              }}
            />
          )}

          {/* IFrame Code */}
          {features.iframes && (
            <IFrameCodeButton
              onOpenIFrameCodeModal={() => {
                if (eventHandlers?.openIFrameCodeModal) {
                  eventHandlers.openIFrameCodeModal();
                } else {
                  eventHandlers?.handleSubmitIFrameCode?.();
                }
              }}
            />
          )}

          {/* Table */}
          {features.tables && (
            <TableButton
              onOpenInsertTableModal={() => {
                if (eventHandlers?.openInsertTableModal) {
                  eventHandlers.openInsertTableModal();
                } else {
                  eventHandlers?.handleInsertTable?.({ rows: 2, cols: 2 });
                }
              }}
            />
          )}
        </div>
      )}

      {/* Table Toolbar for table selections */}
      {features.tables && (
        <EditorTableToolbar
          editor={editor}
          features={features}
        />
      )}
    </div>
  );
};
